/**
 * O portão de entrada.
 *
 * Envolve o app: enquanto não há sessão resolvida pelo servidor, ele ocupa a
 * tela; assim que há, sai da frente e renderiza os filhos. Nada da árvore de
 * dentro monta antes disso, e essa é a razão de existir do componente — o App
 * abre sessão de voz e stream no primeiro efeito, e sessão de voz sem tenant é
 * exatamente o que a v0.2 veio apagar.
 *
 * Não há campo de empresa nem de workspace nesta tela, e isso é intencional a
 * ponto de estar escrito na interface: o cliente diz quem é, o servidor decide
 * onde isso o coloca.
 *
 * As cores saem todas de DOME_PALETTE, como no painel. O portão divide a
 * identidade visual com o resto do app; paleta própria aqui seriam duas
 * identidades brigando logo na primeira tela.
 */

import { useState, type CSSProperties, type FormEvent, type ReactElement, type ReactNode } from 'react';
import { DOME_PALETTE } from '@will/contracts';
import { useAuth } from './useAuth';

export interface LoginGateProps {
  /** o app; só monta depois de haver principal */
  children: ReactNode;
  /** marca exibida na tela de entrada */
  titulo?: string;
  /** uma linha sobre o que é isto, logo abaixo da marca */
  subtitulo?: string;
  /** substitui a tela de espera padrão, para quem quiser outra */
  aguardando?: ReactNode;
}

export function LoginGate({
  children,
  titulo = 'Will',
  subtitulo = 'Entre para começar a conversa.',
  aguardando,
}: LoginGateProps): ReactElement {
  const { principal, loading, error, login, autenticado, fase } = useAuth();
  const [email, setEmail] = useState('');

  // A primeira consulta ao servidor não é "não logado": é "ainda não sei".
  // Mostrar a tela de entrada aqui faria o login piscar para quem já tem sessão.
  if (fase === 'restaurando') {
    return (
      <div className="u1-gate" style={PALETTE_VARS}>
        <style>{GATE_CSS}</style>
        {aguardando ?? (
          <div className="u1-gate__card u1-gate__card--calmo" role="status">
            <span className="u1-gate__marca">{titulo}</span>
            <p className="u1-gate__espera">Verificando quem é você…</p>
          </div>
        )}
      </div>
    );
  }

  if (autenticado && principal !== null) {
    return <>{children}</>;
  }

  const aoEnviar = (evento: FormEvent<HTMLFormElement>): void => {
    evento.preventDefault();
    const limpo = email.trim();
    if (limpo === '') return;
    // O modo de desenvolvimento é o único provedor que existe hoje. Quando
    // houver um real, esta linha vira a escolha entre os dois — e o servidor
    // continua sendo quem recusa o que não deve aceitar.
    void login({ modo: 'dev', email: limpo });
  };

  return (
    <div className="u1-gate" style={PALETTE_VARS}>
      <style>{GATE_CSS}</style>

      <section className="u1-gate__card" aria-labelledby="u1-gate-titulo">
        <header className="u1-gate__head">
          <span className="u1-gate__marca">{titulo}</span>
          <span className="u1-chip" style={{ '--chip': DOME_PALETTE.pending } as CSSProperties}>
            <span className="u1-dot" aria-hidden="true" />
            sem sessão
          </span>
        </header>

        <h1 className="u1-gate__titulo" id="u1-gate-titulo">
          Quem está aí?
        </h1>
        <p className="u1-gate__sub">{subtitulo}</p>

        <form className="u1-gate__form" onSubmit={aoEnviar}>
          <label className="u1-gate__rotulo" htmlFor="u1-gate-email">
            E-mail
          </label>
          <input
            id="u1-gate-email"
            className="u1-gate__campo"
            type="email"
            name="email"
            autoComplete="username"
            inputMode="email"
            placeholder="voce@willone.com.br"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            required
          />

          <button type="submit" className="u1-gate__botao" disabled={loading || email.trim() === ''}>
            {loading ? 'Entrando…' : 'Entrar'}
          </button>
        </form>

        {error !== null && (
          <p className="u1-gate__erro" role="alert">
            {error}
          </p>
        )}

        {/* O aviso fica visível de propósito: esconder o botão no cliente seria
            segurança de fachada — quem recusa o login de desenvolvimento é o
            servidor (DEV_AUTH_BYPASS), e ele recusa mesmo que o botão suma. */}
        <p className="u1-gate__nota">
          <strong>Login de desenvolvimento.</strong> Ainda não há provedor de identidade:
          o servidor aceita este e-mail apenas fora de produção. Nenhuma senha é pedida
          porque nenhuma seria verificada.
        </p>

        <p className="u1-gate__nota u1-gate__nota--fraca">
          Você não escolhe empresa nem workspace aqui. Quem resolve isso é o servidor,
          a partir de quem você é.
        </p>
      </section>
    </div>
  );
}

/**
 * Identidade em vigor, para o cabeçalho do app.
 *
 * Mora aqui porque é a mesma paleta, o mesmo chip e a mesma sessão do portão —
 * e porque a alternativa seria a casca do app reconstruir isto a partir do
 * `principal`, que é justamente como um tenant errado aparece na tela sem
 * ninguém notar.
 */
export function IdentidadeAtual({ className }: { className?: string }): ReactElement | null {
  const { principal, logout, loading } = useAuth();
  if (principal === null) return null;

  const tenant = principal.tenant.name ?? principal.tenant.slug ?? principal.tenant.id;
  const workspace = principal.workspace?.name ?? principal.workspace?.slug ?? null;
  const quem = principal.user.name ?? principal.user.email ?? principal.user.id;

  return (
    <div className={className ? `u1-ident ${className}` : 'u1-ident'} style={PALETTE_VARS}>
      <style>{GATE_CSS}</style>
      <span className="u1-chip" style={{ '--chip': DOME_PALETTE.calm } as CSSProperties}>
        <span className="u1-dot" aria-hidden="true" />
        {tenant}
        {workspace !== null ? ` · ${workspace}` : ''}
      </span>
      <span className="u1-ident__quem">{quem}</span>
      <button
        type="button"
        className="u1-link"
        onClick={() => {
          void logout();
        }}
        disabled={loading}
      >
        sair
      </button>
    </div>
  );
}

export default LoginGate;

// ---------------------------------------------------------------------------
// Estilo
// ---------------------------------------------------------------------------

/** Mesma técnica do painel: a paleta entra como variável, o CSS não repete cor. */
const PALETTE_VARS = {
  '--u1-signal': DOME_PALETTE.signal,
  '--u1-growth': DOME_PALETTE.growth,
  '--u1-alert': DOME_PALETTE.alert,
  '--u1-pending': DOME_PALETTE.pending,
  '--u1-calm': DOME_PALETTE.calm,
} as CSSProperties;

const GATE_CSS = `
.u1-gate, .u1-ident {
  --u1-bg: #0a0d12;
  --u1-surface: #11161d;
  --u1-line: #1e2731;
  --u1-text: #dfe7ef;
  --u1-dim: #8496a8;
  box-sizing: border-box;
  color: var(--u1-text);
  font: 14px/1.5 system-ui, -apple-system, "Segoe UI", sans-serif;
}
.u1-gate *, .u1-gate *::before, .u1-gate *::after,
.u1-ident *, .u1-ident *::before, .u1-ident *::after { box-sizing: inherit; }

.u1-gate {
  display: flex; align-items: center; justify-content: center;
  min-height: 100vh; min-height: 100dvh; padding: 24px;
  background: var(--u1-bg);
}

.u1-gate__card {
  display: flex; flex-direction: column; gap: 14px;
  width: 100%; max-width: 420px; padding: 26px;
  border: 1px solid var(--u1-line); border-radius: 14px;
  background: var(--u1-surface);
}
.u1-gate__card--calmo { align-items: center; gap: 10px; }

.u1-gate__head { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.u1-gate__marca { font-size: 15px; font-weight: 600; letter-spacing: .08em; text-transform: uppercase; color: var(--u1-signal); }
.u1-gate__espera { margin: 0; color: var(--u1-dim); font-size: 13px; }

.u1-gate__titulo { margin: 0; font-size: 22px; font-weight: 600; letter-spacing: -.01em; }
.u1-gate__sub { margin: -6px 0 0; color: var(--u1-dim); font-size: 13px; }

.u1-gate__form { display: flex; flex-direction: column; gap: 8px; margin-top: 6px; }
.u1-gate__rotulo {
  font-size: 11px; font-weight: 600; letter-spacing: .09em; text-transform: uppercase;
  color: var(--u1-dim);
}
.u1-gate__campo {
  padding: 11px 13px; border-radius: 10px;
  border: 1px solid var(--u1-line); background: var(--u1-bg);
  color: inherit; font: inherit;
  transition: border-color .15s;
}
.u1-gate__campo:hover { border-color: color-mix(in srgb, var(--u1-signal) 45%, var(--u1-line)); }
.u1-gate__campo:focus-visible { outline: 2px solid var(--u1-signal); outline-offset: 2px; }
.u1-gate__campo:disabled { opacity: .55; }

.u1-gate__botao {
  margin-top: 4px; padding: 11px 16px; border-radius: 10px;
  border: 1px solid var(--u1-signal);
  background: color-mix(in srgb, var(--u1-signal) 16%, transparent);
  color: var(--u1-signal); font: inherit; font-weight: 600; cursor: pointer;
  transition: background .15s;
}
.u1-gate__botao:hover:not(:disabled) { background: color-mix(in srgb, var(--u1-signal) 26%, transparent); }
.u1-gate__botao:focus-visible { outline: 2px solid var(--u1-signal); outline-offset: 2px; }
.u1-gate__botao:disabled { opacity: .45; cursor: not-allowed; }

.u1-gate__erro {
  margin: 0; padding: 11px 13px; border-radius: 10px;
  border: 1px solid color-mix(in srgb, var(--u1-alert) 40%, transparent);
  background: color-mix(in srgb, var(--u1-alert) 10%, transparent);
  color: color-mix(in srgb, var(--u1-alert) 85%, #ffffff);
  font-size: 12.5px;
}

.u1-gate__nota { margin: 0; color: var(--u1-dim); font-size: 11.5px; }
.u1-gate__nota strong { color: var(--u1-pending); font-weight: 600; }
.u1-gate__nota--fraca { opacity: .8; }

.u1-ident { display: inline-flex; align-items: center; gap: 10px; }
.u1-ident__quem { color: var(--u1-dim); font-size: 12px; }

/* chip e link idênticos aos do painel: uma linguagem só para estado e ação */
.u1-chip {
  display: inline-flex; align-items: center; gap: 6px; flex: none;
  padding: 3px 9px; border-radius: 999px;
  border: 1px solid color-mix(in srgb, var(--chip) 35%, transparent);
  background: color-mix(in srgb, var(--chip) 12%, transparent);
  color: var(--chip);
  font-size: 11px; white-space: nowrap;
}
.u1-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--chip); box-shadow: 0 0 8px var(--chip); }

/* .u1-link mora no App.css, e SO la.
   Esta folha injetada tinha a propria copia, e o Panel.tsx tinha uma terceira.
   Em 21/08 o alvo de toque do "sair" foi corrigido no App.css, medido de novo,
   e continuou 22px — porque a copia daqui, injetada depois, ganhava. Corrigido
   no Panel, medido de novo, ainda 22px: faltava esta.
   Tres definicoes da mesma classe, e o conserto certo em UM lugar de tres
   respondia "esta certo?" com sim e deixava o defeito. E o mesmo erro de
   ALCANCE que a semana inteira cobrou, agora em CSS.
   Sobrou so o que e local desta tela. */
.u1-link { font-size: 12px; }
.u1-link:disabled { opacity: .5; cursor: not-allowed; }
.u1-link:focus-visible { outline: 2px solid var(--u1-signal); outline-offset: 2px; }

@media (prefers-reduced-motion: reduce) {
  .u1-gate__campo, .u1-gate__botao { transition: none; }
}
`;
